import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SelectItem } from 'primeng';
import { DataConcessionScenario } from '../concession-basic-profile.component';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
  ConcessionRegistrationDetails,
  ConcessionRegistrationDetailsApiModel,
  ConcessionManagementDataValidationInitial,
} from '../../concession-registration.models';
import { ConcessionRegistrationService } from '../../concession-registration.service';
import { catchError, debounceTime, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { ErrorService } from 'src/app/services/error/error.service';
import { mapCommodityToKey, mapRoleToKey } from '../../concession-registration.mappers';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { Observable, of } from 'rxjs';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { DataConcessionValidationService } from './data-concession-validation.service';
import { CommonService } from 'src/app/services/common.service';

export type ConcessionDataValidationFormUpdateModel = { formValue: any; preventUpdate?: boolean };
export type ChangeCommodityModel = { commodity: string; selected: boolean };

@Component({
  selector: 'app-data-concession-validation',
  templateUrl: './data-concession-validation.component.html',
  styleUrls: ['./data-concession-validation.component.scss'],
  providers: [TranslatePipe],
})
export class DataConcessionValidationComponent extends DestroyableComponent implements OnInit, AfterViewInit {
  @Input() set initialValue(value: ConcessionManagementDataValidationInitial) {
    this.formInitialValues = value;
  }

  constructor(
    private registrationGopService: ConcessionRegistrationService,
    private concessionRegistrationService: ConcessionRegistrationService,
    private translate: TranslatePipe,
    private errorService: ErrorService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private netzTranslateService: NetzTranslateService,
    private dataConcessionValidationService: DataConcessionValidationService,
    private commonService: CommonService
  ) {
    super();
    this.showConcessionDistrictHeating = environmentService.showConcessionDistrictHeating;
  }
  @Input() scenario: DataConcessionScenario;
  @Input() firstName: string;
  @Input() lastName: string;

  @Output() onStatusChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onChangeCommodity: EventEmitter<ChangeCommodityModel> = new EventEmitter<ChangeCommodityModel>();

  form: FormGroup;
  formInitialValues: ConcessionManagementDataValidationInitial;

  technicalRoleOptions: SelectItem[] = [
    { label: 'Techniian', value: 'standard_technician' },
    { label: 'Guest Technician', value: 'guest_technician' },
  ];

  comodities: string[] = ['power', 'gas', 'water', 'heating'];
  showMastersExaminationOn: boolean = true;
  dateFormat: string = 'dd.MM.yyyy';
  occupationOptions: SelectItem[] = [
    { label: 'select', value: '' },
    { label: 'master', value: 'Meister' },
    { label: 'engineer', value: 'Ingenieur' },
    { label: 'craft', value: 'Meister In anderem Handwerk' },
    { label: 'journeyman', value: 'Altgeselle' },
    { label: 'exemption', value: 'Ausnahmebewilligung' },
  ];
  technicianBdewDetailsCache = {};

  isConcessionRegistrationDetailsLoading: boolean;
  showConcessionDistrictHeating: boolean;

  formValidators = {
    validateRegisteredCommodities: (): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        const isFieldApplicable = this.scenario === 'Manual';

        if (!isFieldApplicable) {
          return null;
        }

        const values = Object.values(control.value);

        if (values.indexOf(true) === -1) {
          return {
            ['required']: 'At least one comodity must be selected.',
          };
        }

        return null;
      };
    },

    validateHasWeldingLicense: (): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!this.form) {
          return;
        }

        const isFieldApplicable =
          (this.scenario === 'Mixed' && this.showConcessionDistrictHeating && this.form.controls.isRegisterForDistrictHeating.value) ||
          this.scenario === 'Manual';

        if (!isFieldApplicable || !this.isNullOrUndefined(control.value)) {
          return null;
        }

        return {
          ['required']: 'Welding license is required.',
        };
      };
    },

    validateTechnicalRole: () => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (this.scenario === 'Manual' && !control.value) {
          return {
            ['required']: 'Technical roles is required',
          };
        }

        return null;
      };
    },

    validateOccupation: () => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!this.form) {
          return;
        }

        const isApplicable =
          this.scenario === 'Manual' ||
          (this.scenario === 'Mixed' && this.showConcessionDistrictHeating && this.form.controls.isRegisterForDistrictHeating.value);

        if (isApplicable && !control.value) {
          return {
            ['required']: 'Occupation is required',
          };
        }

        return null;
      };
    },

    validateBdewTechnicianId: () => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!this.form) {
          return;
        }

        const isApplicable = this.scenario === 'Mixed' && this.form.controls.hasBdewId.value;

        if (!isApplicable) {
          return null;
        }

        if (!control.value) {
          return {
            ['required']: 'BDEW technician ID is required',
          };
        }

        const bdewIdRegExpr = /^[a-zA-Z0-9]+$/g;

        if (!bdewIdRegExpr.test(control.value as string)) {
          return {
            ['invalidFormat']: 'BDEW-ID must be numbers and letters, e.g. I0M012345',
          };
        }

        return null;
      };
    },

    validateTopLevelSelection: () => {
      return (control: FormGroup): ValidationErrors | null => {
        const hasBdewId = control.controls.hasBdewId.value as boolean;
        const isRegisterForDistrictHeating = control.controls.isRegisterForDistrictHeating.value as boolean;

        const isAllSetToNo = this.showConcessionDistrictHeating ? !hasBdewId && !isRegisterForDistrictHeating : !hasBdewId;

        if (this.scenario === 'Mixed' && isAllSetToNo) {
          return {
            ['invalid']: 'Either provide bdewId or select register for heating',
          };
        }

        return null;
      };
    },

    validateTechnicianRegistrationDetails: () => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!this.form || this.scenario !== 'Mixed') {
          return;
        }

        if (this.form.controls.hasBdewId.value && !control.value) {
          return {
            ['invalid']: "Technician registration details hasn't been loaded",
          };
        }

        return null;
      };
    },
  };

  ngAfterViewInit(): void {
    this.form.updateValueAndValidity({ emitEvent: false });
    this.onStatusChange.next(this.form.status?.toLowerCase() === 'valid');
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.form = new FormGroup(
      {
        role: new FormControl(this.formInitialValues.role, this.scenario === 'Manual' ? [Validators.required] : []),
        occupation: new FormControl(this.formInitialValues.occupation, [this.formValidators.validateOccupation()]),
        registeredCommodities: new FormGroup(
          {
            power: new FormControl(this.formInitialValues.registeredCommodities?.power, []),
            gas: new FormControl(this.formInitialValues.registeredCommodities?.gas, []),
            heating: new FormControl(this.formInitialValues.registeredCommodities?.heating, []),
            water: new FormControl(this.formInitialValues.registeredCommodities?.water, []),
          },
          [this.formValidators.validateRegisteredCommodities()]
        ),
        hasWeldingLicense: new FormControl(this.formInitialValues.hasWeldingLicense, [this.formValidators.validateHasWeldingLicense()]),
        hasBdewId: new FormControl(!!this.formInitialValues.bdewTechnicianId, this.scenario === 'Mixed' ? [Validators.required] : []),
        isRegisterForDistrictHeating: new FormControl(
          this.formInitialValues.isRegisterForDistrictHeating,
          this.scenario === 'Mixed' && this.showConcessionDistrictHeating ? [Validators.required] : []
        ),
        bdewTechnicianId: new FormControl(this.formInitialValues.bdewTechnicianId, [this.formValidators.validateBdewTechnicianId()]),
        concessionRegistrationDetails: new FormControl(null, [this.formValidators.validateTechnicianRegistrationDetails()]),
      },
      [this.formValidators.validateTopLevelSelection()]
    );

    this.dataConcessionValidationService.form = this.form;

    if (this.scenario === 'Mixed') {
      this.loadTechnicianDetails(this.form, true);
    }

    for (const c of this.comodities) {
      (this.form.controls.registeredCommodities as FormGroup).addControl(c, new FormControl(false));
    }

    this.preventLeak(this.form.valueChanges).subscribe(formValue => {
      Object.keys(this.form.controls).forEach(field => {
        this.form.controls[field].updateValueAndValidity({ emitEvent: false });
      });

      this.onStatusChange.next(this.form.status?.toLowerCase() === 'valid');
    });

    this.preventLeak(this.form.valueChanges)
      .pipe(debounceTime(500))
      .subscribe(formValue => {
        this.concessionRegistrationService.updateConcessionValidationForm({ formValue, preventUpdate: false });
        this.onStatusChange.next(this.form.status?.toLowerCase() === 'valid');
      });

    this.preventLeak(this.form.statusChanges).subscribe(status => {
      this.onStatusChange.next(status?.toLowerCase() === 'valid');
    });

    this.preventLeak(this.form.controls.hasBdewId.valueChanges).subscribe(value => {
      if (!value) {
        this.form.controls.bdewTechnicianId.setValue('');
      }
    });

    this.dataConcessionValidationService.formPureValue = this.form.value;
    this.concessionRegistrationService.updateConcessionValidationForm({ formValue: this.form.value, preventUpdate: true });
  }

  changeCommodity(commodity: string, event: { checked: boolean }): void {
    this.onChangeCommodity.emit({ commodity, selected: event.checked });
  }

  loadTechnicianDetails(form: FormGroup, preventEvents?: boolean) {
    const bdewTechnicianIdFormControl = form.controls.bdewTechnicianId;

    if (bdewTechnicianIdFormControl.valid && bdewTechnicianIdFormControl.value) {
      this.isConcessionRegistrationDetailsLoading = true;
      this.preventLeak(of(this.technicianBdewDetailsCache))
        .pipe(
          switchMap(cache => {
            if (cache[bdewTechnicianIdFormControl.value]) {
              return of(cache[bdewTechnicianIdFormControl.value]);
            } else {
              if (!this.firstName || !this.lastName) {
                this.errorService.throwErrorIfFailure(
                  {
                    result: 'error',
                    message: this.netzTranslateService.translateString('basic-profile.fields.bdew-missing-technician-name-error'),
                  },
                  true
                );
              } else {
                return this.registrationGopService.loadTechnicianInfo(bdewTechnicianIdFormControl.value, this.firstName, this.lastName);
              }
            }
          })
        )
        .subscribe(
          (response: ConcessionRegistrationDetailsApiModel) => {
            this.technicianBdewDetailsCache[bdewTechnicianIdFormControl.value] = response;
            const concessionRegistrationDetails: ConcessionRegistrationDetails = {
              technicianRole: mapRoleToKey(response.role),
              registeredCommodities: response.commodities.map(c => mapCommodityToKey(c)),
              validUntil: new Date(response.validUntil.year, response.validUntil.month - 1, response.validUntil.day),
            };

            form.controls.concessionRegistrationDetails.setValue(concessionRegistrationDetails, {
              emitEvent: !preventEvents,
            });

            const registeredCommoditiesFormArray = this.form.controls.registeredCommodities as FormArray;
            const commoditiesResponse = response.commodities.map(c => c.toLocaleLowerCase());
            this.comodities.forEach(c => {
              registeredCommoditiesFormArray.controls[c].setValue(commoditiesResponse.indexOf(c) > -1);
            });

            if (preventEvents) {
              this.onStatusChange.next(this.form.status?.toLowerCase() === 'valid');
            }
          },
          error => {
            form.controls.concessionRegistrationDetails.setValue(null, { emitEvent: !preventEvents });
            this.isConcessionRegistrationDetailsLoading = false;
            this.onStatusChange.next(this.form.status?.toLowerCase() === 'valid');
            this.errorService.throwErrorIfFailure(error, true);
          },
          () => {
            this.isConcessionRegistrationDetailsLoading = false;
          }
        );

      return;
    }

    this.form.controls.concessionRegistrationDetails.setValue(null);
  }

  getRegisteredCommodities(): string {
    return this.form.controls.concessionRegistrationDetails.value?.registeredCommodities?.map(c => this.translate.transform(c)).join(' & ');
  }

  private isNullOrUndefined(value: any) {
    return value === null || value === undefined;
  }
}
